/**
 * IsFeatureEnabledForCurrentUser Use Case Implementation (User-facing)
 *
 * Checks if a specific feature is enabled for the current user. The unified
 * `/feature-configs/resolved` endpoint dispatches anonymous vs authenticated
 * resolution by the request's auth state, so the only routing decision here
 * is which `ConfigContext` to send: event-scoped when the feature is
 * anonymous-viewable and an `eventIdPublic` is given (so anonymous callers
 * can resolve event-scoped overrides), otherwise empty.
 *
 * Two-level enabled system: a feature is enabled only when both the cascade
 * `enabled` flag and the config's `active` flag are true.
 */

import type { EventIdPublic } from "@shared/schemas/branded-id.schemas";
import {
  FeatureKey,
  FeatureKeySchema,
} from "@shared/schemas/feature-key.schemas";
import { FeatureAccessControlRegistry } from "@shared/registries/feature-access-control.registry";
import { useMemo } from "react";
import { useUserActions } from "@frontend/shared/features/actions/application/actions/use-user-actions";

export interface IsFeatureEnabledForCurrentUserUseCaseInput {
  readonly featureKey: FeatureKey;

  readonly eventIdPublic?: EventIdPublic | undefined;

  readonly enabled?: boolean | undefined;
}

export interface IsFeatureEnabledForCurrentUserUseCaseOutput {
  readonly isEnabled: boolean;

  readonly isLoading: boolean;

  readonly error: Error | null;
}

export type IsFeatureEnabledForCurrentUserUseCase = (
  input: IsFeatureEnabledForCurrentUserUseCaseInput,
) => IsFeatureEnabledForCurrentUserUseCaseOutput;

function isAnonymousViewable(featureKey: FeatureKey): boolean {
  const parsed = FeatureKeySchema.parse(featureKey);
  const accessControl = FeatureAccessControlRegistry.get(parsed);
  return accessControl.viewableBy === null;
}

export function createIsFeatureEnabledForCurrentUserUseCase(): IsFeatureEnabledForCurrentUserUseCase {
  return (
    input: IsFeatureEnabledForCurrentUserUseCaseInput,
  ): IsFeatureEnabledForCurrentUserUseCaseOutput => {
    const { featureKey, eventIdPublic, enabled = true } = input;

    const useEventContext =
      eventIdPublic !== undefined && isAnonymousViewable(featureKey);

    const {
      data: configs,
      isLoading,
      error,
    } = useUserActions().useGetFeatureConfigs({
      context: useEventContext ? { eventIdPublic } : {},
      enabled,
    });

    const isEnabled = useMemo((): boolean => {
      if (configs === undefined) return false;
      const config = configs.find((c) => c.featureKey === featureKey);
      if (config === undefined) return false;
      return config.enabled && config.config.active === true;
    }, [configs, featureKey]);

    return {
      isEnabled,
      isLoading,
      error: error ?? null,
    };
  };
}

export const useIsFeatureEnabledForCurrentUser =
  createIsFeatureEnabledForCurrentUserUseCase();
